/**
 * Maximum number of characters accepted for a user's first or last name
 * across signup, avatar setup and profile editing.
 */
export const NAME_MAX_LENGTH = 8;

export const NAME_LIMIT_MESSAGE = `Name cannot be longer than ${NAME_MAX_LENGTH} characters`;

/** Keys that add a character; everything else (arrows, backspace, shortcuts) is harmless. */
const isPrintableKey = (e) => {
  if (!e || e.ctrlKey || e.metaKey || e.altKey) return false;
  // Android soft keyboards report every keystroke as Unidentified / keyCode 229.
  if (e.key === 'Unidentified' || e.keyCode === 229) return true;
  return e.key?.length === 1;
};

/** True when adding `addedLength` characters would push the field past the cap. */
const wouldExceedLimit = (input, addedLength) => {
  if (!input) return false;
  const selectionLength = Math.abs((input.selectionEnd || 0) - (input.selectionStart || 0));
  return ((input.value?.length || 0) - selectionLength + addedLength) > NAME_MAX_LENGTH;
};

// Key repeat would otherwise re-fire the snackbar on every keystroke.
let lastNotifiedAt = 0;
const notifyOnce = (notify) => {
  const now = Date.now();
  if (now - lastNotifiedAt < 1200) return;
  lastNotifiedAt = now;
  notify();
};

/**
 * Props to spread onto a first/last name input so the user is told why their
 * typing stopped once the field is full. Pair it with maxLength={NAME_MAX_LENGTH},
 * which does the actual truncating.
 *
 * @param {() => void} notify - shows the error snackbar
 */
export const nameLimitHandlers = (notify) => ({
  onKeyDown: (e) => {
    if (isPrintableKey(e) && wouldExceedLimit(e.target, 1)) notifyOnce(notify);
  },
  onPaste: (e) => {
    const pasted = e.clipboardData?.getData('text') || '';
    if (wouldExceedLimit(e.target, pasted.length)) notifyOnce(notify);
  },
});

export default NAME_MAX_LENGTH;
